import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  useTheme,
  TextField,
  Skeleton,
} from '@mui/material';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../exo/ExoDialog';

const pdfOptions = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};

const PageSelectionDialog = ({
  open,
  onClose,
  pdfFile,
  pageCount,
  onConfirm,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [selectedPages, setSelectedPages] = useState([]);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const pdfUrl = useMemo(() => {
    if (pdfFile) {
      return URL.createObjectURL(pdfFile);
    }
    return null;
  }, [pdfFile]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (open && pageCount > 0) {
      setSelectedPages(Array.from({ length: pageCount }, (_, i) => i + 1));
    }
  }, [open, pageCount]);

  const togglePage = (pageNumber) => {
    setSelectedPages((prev) =>
      prev.includes(pageNumber)
        ? prev.filter((p) => p !== pageNumber)
        : [...prev, pageNumber].sort((a, b) => a - b)
    );
  };

  const selectAll = () => {
    setSelectedPages(Array.from({ length: pageCount }, (_, i) => i + 1));
  };

  const selectNone = () => {
    setSelectedPages([]);
  };

  const selectRange = () => {
    const start = parseInt(rangeStart, 10);
    const end = parseInt(rangeEnd, 10);
    if (!isNaN(start) && !isNaN(end) && start >= 1 && end <= pageCount && start <= end) {
      const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      setSelectedPages(range);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedPages);
  };

  if (!pdfUrl) return null;

  return (
    <ExoDialog open={open} onClose={onClose} maxWidth="1000px" limitWidth>
      <Box
        className="flex flex-col h-full overflow-hidden rounded-lg"
        sx={{ bgcolor: colors.glass }}
      >
        <Box
          className="px-4 py-3"
          sx={{
            bgcolor: colors.card,
            borderBottom: `1px solid ${colors.selected}`,
          }}
        >
          <Typography variant="h6">
            {t('Select pages')}
          </Typography>
        </Box>

        <Box className="flex-1 overflow-auto p-4" sx={{ bgcolor: colors.paper }}>
          <Box
            className="grid gap-3"
            sx={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            }}
          >
            {Array.from({ length: pageCount }, (_, index) => {
              const pageNumber = index + 1;
              const isSelected = selectedPages.includes(pageNumber);

              return (
                <Box
                  key={pageNumber}
                  onClick={() => togglePage(pageNumber)}
                  className="cursor-pointer relative"
                  sx={{
                    border: isSelected
                      ? `2px solid ${colors.info}`
                      : `1px solid ${colors.selected}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: colors.bgInput,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: colors.info,
                      bgcolor: colors.card,
                    },
                  }}
                >
                  <Box
                    className="absolute top-0 left-0 z-10"
                    sx={{ bgcolor: 'transparent' }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: colors.selected,
                        '&.Mui-checked': {
                          color: colors.info,
                        },
                      }}
                    />
                  </Box>
                  <Document
                    file={pdfUrl}
                    options={pdfOptions}
                    loading={
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={140}
                        sx={{ bgcolor: colors.bgInput }}
                      />
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={100}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                  <Typography
                    variant="caption"
                    className="text-center block"
                    sx={{
                      py: 0.5,
                      bgcolor: isSelected ? colors.info + '20' : colors.glassSolid,
                      color: colors.text,
                    }}
                  >
                    {pageNumber}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          className="px-4 py-3 flex flex-col sm:flex-row justify-between items-center gap-3"
          sx={{
            bgcolor: colors.card,
            borderTop: `1px solid ${colors.selected}`,
          }}
        >
          <Box className="flex items-center gap-2 flex-wrap">
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.7 }}>
              {t('Quick selection')}:
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={selectAll}
              sx={{ color: colors.text, minWidth: 'auto' }}
            >
              {t('All')}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={selectNone}
              sx={{ color: colors.text, minWidth: 'auto' }}
            >
              {t('None')}
            </Button>
            <Box className="flex items-center gap-1">
              <TextField
                size="small"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                placeholder="1"
                sx={{
                  width: 45,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.bgInput,
                  },
                }}
                inputProps={{ style: { textAlign: 'center', padding: '4px' } }}
              />
              <Typography variant="body2" sx={{ color: colors.text, opacity: 0.5 }}>-</Typography>
              <TextField
                size="small"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                placeholder={String(pageCount)}
                sx={{
                  width: 45,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.bgInput,
                  },
                }}
                inputProps={{ style: { textAlign: 'center', padding: '4px' } }}
              />
              <Button
                size="small"
                variant="text"
                onClick={selectRange}
                sx={{ color: colors.info, minWidth: 'auto' }}
              >
                OK
              </Button>
            </Box>
          </Box>

          <Box className="flex items-center gap-3">
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.7 }}>
              {selectedPages.length} {t('pages selected')}
            </Typography>
            <Button
              variant="text"
              onClick={onClose}
              sx={{ color: colors.text }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={selectedPages.length === 0}
              sx={{
                bgcolor: colors.info,
                '&:hover': {
                  bgcolor: colors.info + 'dd',
                },
              }}
            >
              {t('Continue')}
            </Button>
          </Box>
        </Box>
      </Box>
    </ExoDialog>
  );
};

export default PageSelectionDialog;
